import Card from '../components/card/Card';
import { useEffect, useState } from 'react';
import H from '../components/texts/h/H';
import P from '../components/texts/p/P';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import PostBalanceActivity from '../organisms/Risk/PostBalance/PostBalanceActivity';
import Icon from '../components/icon/Icon';
import CheckLight from '../organisms/Risk/CheckLight/Checklight';
import { getApplication } from '../services/ApplicationService';
import { balanceInfo, clientData, indicator, riskData } from '../types/interfaces';
import { Toast, useToast } from '../components/toast/toast';
import { BALANCE_STATUS_MESSAGES, getBalanceProcessingErrorMessage } from '../utils/balanceReadStatusUtils';
import {
  getBalanceSheetsByCuit,
  getBalanceInfoByCode,
  postRiskData,
  putRiskData,
  getDebts,
  saveDebts,
  updateDebts,
  getRiskAnalysisWithDebts,
} from '../services/RiskService';
import BalanceLoader from '../organisms/Risk/BalanceLoader';
import DualBalanceLoader from '../organisms/Risk/DualBalanceLoader';
import RiskResume from '../organisms/Risk/Resume';
import { initialClientData, RISK_CODES } from '../utils/Constants';
import {
  capitalize,
  hasData,
  hasError,
  isSuccessfulResponse,
  getEnabledRiskCodes,
} from '../utils/Utils';
import { standardizeBalanceData } from '../utils/BalanceDataUtils';
import { StandardizedBalanceData } from '../types/interfaces';
import Step from '../components/Step/Step';
import useStatus from '../hooks/useStatus';
import useStepNavigation from '../hooks/useStepNavigation';
import {
  DASHBOARD_KEYS,
  navigateBackToList,
  resolveListRoute,
  type DetailLocationState,
} from '../utils/listNavigationState';
import { cuitMask } from '../utils/Masks';
import { useConfig } from '../context/ConfigContext';
import { getApplicationStatusKey } from '../utils/applicationStatusUtils';
import theme from '../styles/theme';

type CombinedRiskData = Partial<riskData> & {
  riskCode?: string;
  executionDate?: string;
  previousBpbIndicators?: indicator[];
};

const normalizeRiskData = (risks: CombinedRiskData[]): riskData[] =>
  risks.map((risk) => ({
    ...risk,
    risk_code: risk.risk_code ?? risk.riskCode,
    execution_date: risk.execution_date ?? risk.executionDate,
    previous_bpb_indicators:
      risk.previous_bpb_indicators ?? risk.previousBpbIndicators,
  })) as riskData[];

const RiskAnalysis = ({
  showStepBar = true,
  showOnlyRiskResume = false,
  hideCommiteInfo = false,
}: {
  showStepBar?: boolean;
  showOnlyRiskResume?: boolean;
  hideCommiteInfo?: boolean;
}): JSX.Element => {
  const { cuit, id } = useParams();
  const location = useLocation();
  const detailState = (location.state ?? {}) as DetailLocationState;
  const status = detailState.status;
  const statusCode = detailState.statusCode;
  const listState = detailState.listState;
  const applicationStatusKey = getApplicationStatusKey(
    statusCode ?? status ?? ''
  );
  const { isInCommittee } = useStatus({
    applicationStatus: applicationStatusKey,
  });
  const config = useConfig();
  const isUruguayEnabled = config?.isUruguayEnabled === true;
  /** Strict Argentina allowlist for NOSIS/CASFOG/BCRA (not !isUruguayEnabled). */
  const isArgentinaEnabled = config?.isArgentinaEnabled === true;
  /** Paso 1 Semáforo: LIDECO (UY) vs Nosis/CASFOG/BCRA (AR only). */
  const paso1RiskCodes = () =>
    getEnabledRiskCodes(
      isArgentinaEnabled
        ? [RISK_CODES.nosis, RISK_CODES.casfog, RISK_CODES.bcra]
        : isUruguayEnabled
          ? [RISK_CODES.lideco]
          : []
    );
  /** BPB sólo puede calcularse cuando la carpeta ya tiene un balance válido. */
  const folderRiskCodes = (hasBalance: boolean) =>
    getEnabledRiskCodes(
      isArgentinaEnabled
        ? [
            RISK_CODES.nosis,
            RISK_CODES.casfog,
            RISK_CODES.bcra,
            ...(hasBalance ? [RISK_CODES.bpb] : []),
          ]
        : isUruguayEnabled
          ? [RISK_CODES.lideco, ...(hasBalance ? [RISK_CODES.bpb] : [])]
          : hasBalance
            ? [RISK_CODES.bpb]
            : []
    );
  const [currentStep, setCurrentStep] = useState(detailState ? 1 : 1);
  const navigate = useNavigate();

  const handleGoBackToList = () => {
    navigateBackToList(
      navigate,
      listState,
      resolveListRoute(DASHBOARD_KEYS.risk),
    );
  };
  const [riskData, setRiskData] = useState<riskData | undefined>(undefined);
  const [balanceData, setBalanceData] = useState<StandardizedBalanceData | undefined>(
    undefined
  );
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [balanceInfo, setBalanceInfo] = useState<balanceInfo | undefined>(
    undefined
  );
  const [purchasesSalesData, setPurchasesSalesData] = useState({});
  const [postBalanceData, setPostBalanceData] = useState({});
  const [client, setClient] = useState<clientData>(initialClientData);
  const [localBalanceId, setLocalBalanceId] = useState<number>(0);
  const [debts, setDebts] = useState<any>({});
  const [riskAnalysisId, setRiskAnaliysisId] = useState<number>();

  // Dual balance (Cementos): solo visible cuando el flag de configuración lo habilita
  const showDualBalance = config.isShowDualBalanceEnabled;
  const isMaxCreditCalculatorEnabled: boolean = config.isMaxCreditCalculatorEnabled === true;
  const isQuotationsEnabled: boolean = config.isQuotationsEnabled === true;
  const [previousBalanceInfo, setPreviousBalanceInfo] = useState<balanceInfo | undefined>(
    undefined
  );
  const [previousLocalBalanceId, setPreviousLocalBalanceId] = useState<number>(0);
  const [previousBpbIndicators, setPreviousBpbIndicators] = useState<indicator[] | undefined>(
    undefined
  );

  // Hook para manejar la navegación automática de pasos
  const {
    maxStepReached,
    updateMaxStepReached,
    navigateToStep,
    goToNextStep,
    goToPreviousStep,
  } = useStepNavigation({
    cuit,
    id,
    moduleName: 'riskAnalysis',
    totalSteps: 4, // 4 steps (flujo unificado para todos los clientes)
    currentStep,
    setCurrentStep,
  });

  // Use the same totalSteps value for navigation checks
  const totalStepsForNavigation = 4;

  // Carga de aplicación al montar, cuando cambian cuit/id o al reentrar a la ruta (p. ej. tras editar en originación).
  useEffect(() => {
    if (cuit && id) {
      getApplication(cuit, id).then((response) => {
        if (hasData(response)) {
          if (response.data.balance_processing_status === 'PROCESSING') {
            useToast.warning({
              title: 'Balance en proceso',
              id: 'risk-toast',
              description: BALANCE_STATUS_MESSAGES.PROCESSING,
              closeButton: true,
              autoClose: 5000,
            });
            handleGoBackToList();
            return;
          }

          setClient(response.data);
          setPostBalanceData(response.data.post_balance_sheet_items);

          if (response.data.balance_sheet_code) {
            getBalanceInfoFromCode(cuit, response.data.balance_sheet_code);
            // Si dual balance está habilitado, obtener también el balance anterior
            if (showDualBalance) {
              getPreviousBalanceForDualView(cuit, response.data.balance_sheet_code);
            }
          } else {
            // No hay balance_sheet_code asignado, intentar obtener el más reciente
            getMostRecentBalanceSheets(cuit);
            // Si no hay balance_sheet_code, mostrar mensaje informativo
            useToast.info({
              title: 'No hay balance cargado',
              id: 'risk-toast',
              description: BALANCE_STATUS_MESSAGES.NO_BALANCE_PAGE,
              closeButton: true,
              autoClose: 8000,
            });
          }

          if (
            response.data.balance_processing_status === 'FAILED' &&
            response.data.balance_processing_error_code
          ) {
            const description = getBalanceProcessingErrorMessage(
              response.data.balance_processing_error_code,
              response.data.balance_processing_error_context,
              config?.country
            );
            useToast.error({
              title: 'El balance falló al procesarse',
              id: 'risk-toast',
              description,
              closeButton: true,
              autoClose: 10000,
            });
          }

          if (response.data.risk_analysis) {
            const analysisId = response.data.risk_analysis.id as number;
            setRiskAnaliysisId(analysisId);

            const expectedRiskCodes = folderRiskCodes(
              Boolean(response.data.balance_sheet_code)
            );
            const savedRisks = response.data.risk_analysis.risks as riskData[];
            const savedRisksCodes: string[] = savedRisks.map(
              (risk: { risk_code: string }) => risk.risk_code
            );
            const hasAllRequiredRisks = expectedRiskCodes.every((code) =>
              savedRisksCodes.includes(code)
            );

            // Seed existing risks (e.g. BPB) immediately so Paso 4 Balance
            // does not vanish while we fetch missing codes (e.g. LIDECO).
            setRiskData(savedRisks);
            const bpbRisk = savedRisks?.find((r: riskData) => r.risk_code === 'BPB');
            const prev =
              bpbRisk?.previous_bpb_indicators ??
              (bpbRisk as any)?.previousBpbIndicators;
            setPreviousBpbIndicators(
              Array.isArray(prev) && prev.length > 0 ? prev : undefined
            );

            if (!hasAllRequiredRisks) {
              // Only fetch missing codes (e.g. LIDECO). Keep seeded BPB visible:
              // a blocking isLoading spinner would hide Balance on Paso 4 while
              // a slow/hanging LIDECO PUT runs.
              const missingCodes = expectedRiskCodes.filter(
                (code) => !savedRisksCodes.includes(code)
              );
              setIsLoading(false);
              // Pass analysisId: setState is async; without it handleRiskData
              // would POST and fail when a risk_analysis already exists.
              handleRiskData(missingCodes, false, analysisId, { silent: true });
            } else {
              setIsLoading(false);
            }
          } else {
            handleRiskData(folderRiskCodes(Boolean(response.data.balance_sheet_code)));
          }
        }

        if (isArgentinaEnabled) {
          if (
            hasData(response) &&
            response.data.client_debt_analysis_code === null
          ) {
            saveDebts(cuit, id).then((response) => {
              if (hasData(response) && response.status === 201) {
                setDebts(response.data);
              }
            });
          } else {
            if (hasData(response)) {
              const code = response.data.client_debt_analysis_code;
              getDebts(cuit, code).then((response) => {
                if (hasData(response) && response.status === 200) {
                  setDebts(response.data);
                } else if (hasError(response).isAnyError) {
                  useToast.error({
                    title: 'Ocurrió un error al consultar la información.',
                    id: 'risk-toast',
                    description: hasError(response).isClientError
                      ? 'Error del cliente'
                      : 'Error del servidor',
                    closeButton: true,
                    autoClose: 5000,
                  });
                }
              });
            }
          }
        }
      });
    }
  }, [cuit, id, location.key, isArgentinaEnabled]);

  // Cargar balance anterior para vista dual cuando hay balance actual y aún no hay previous. No incluir getPreviousBalanceForDualView en deps para evitar re-ejecuciones en cada render.
  useEffect(() => {
    if (showDualBalance && cuit && balanceInfo?.code && !previousBalanceInfo) {
      getPreviousBalanceForDualView(cuit, balanceInfo.code);
    }
  }, [showDualBalance, cuit, balanceInfo?.code, previousBalanceInfo]);

  const changeStep = (step: number) => {
    const canNavigate =
      maxStepReached >= totalStepsForNavigation || isInCommittee || currentStep > step;
    // Permitir ir al paso 2 (Carga de balance) aunque no haya balance, para crear uno manualmente
    const canGoToStepWithoutBalance = step === 2 && balanceData === undefined;

    if (canNavigate && (balanceData !== undefined || canGoToStepWithoutBalance)) {
      navigateToStep(step);
    }
  };

  const getBalanceInfoFromCode = (cuit: string, balanceSheetCode: string) => {
    getBalanceInfoByCode(cuit, balanceSheetCode).then((response) => {
      if (hasData(response) && response.status === 200) {
        console.log('[RiskAnalysis] Respuesta completa del API:', response.data);
        console.log('[RiskAnalysis] net_worth en respuesta del API:', response.data.net_worth);
        
        // Usar la función utilitaria para estandarizar los datos (redondeo a entero, task 49267)
        const standardizedData = standardizeBalanceData(response.data);
        setBalanceData(standardizedData);
        setBalanceInfo({
          ...response.data,
          ...formatNumbers(standardizedData.values),
          code: balanceSheetCode,
        });
        setLocalBalanceId(response.data.id);
      } else if (hasError(response).isAnyError) {
        useToast.error({
          title: 'Ocurrió un error al consultar la información del balance.',
          id: 'risk-toast',
          description: hasError(response).isClientError
            ? 'Error del cliente'
            : 'Error del servidor',
          closeButton: true,
          autoClose: 5000,
        });
      }
    });
  };

  const getMostRecentBalanceSheets = (cuit: string) => {
    getBalanceSheetsByCuit(cuit).then((response) => {
      if (hasData(response)) {
        if (isSuccessfulResponse(response)) {
          if (response.data.length > 0) {
            // Sort balances by date (most recent first) to ensure consistent ordering
            const sortedBalances = [...response.data].sort((a: any, b: any) => {
              const dateA = new Date(a.to_year, (a.to_month || 1) - 1);
              const dateB = new Date(b.to_year, (b.to_month || 1) - 1);
              return dateB.getTime() - dateA.getTime();
            });

            // Set current balance (most recent); valores redondeados a entero (task 49267)
            const stdCurrent = standardizeBalanceData(sortedBalances[0]);
            setBalanceInfo({
              ...sortedBalances[0],
              ...formatNumbers(stdCurrent.values),
            });
            setLocalBalanceId(sortedBalances[0].id);

            // Set previous balance if dual balance is enabled and exists
            if (showDualBalance && sortedBalances.length > 1) {
              const stdPrevious = standardizeBalanceData(sortedBalances[1]);
              setPreviousBalanceInfo({
                ...sortedBalances[1],
                ...formatNumbers(stdPrevious.values),
              });
              setPreviousLocalBalanceId(sortedBalances[1].id);
            }
          } else {
            // No hay balances disponibles, mostrar mensaje informativo
            useToast.info({
              title: 'No hay balance cargado',
              id: 'risk-toast',
              description: BALANCE_STATUS_MESSAGES.NO_BALANCE_PAGE,
              closeButton: true,
              autoClose: 8000,
            });
          }
        } else if (hasError(response).isAnyError) {
          useToast.error({
            title: hasError(response).isClientError
              ? 'Ocurrió un error al consultar la información del balance.'
              : 'Ocurrió un error inesperado. Intentá nuevamente.',
            id: 'risk-toast',
            description: hasError(response).isClientError
              ? 'Error del cliente'
              : 'Error del servidor',
            closeButton: true,
            autoClose: 5000,
          });
        }
      }
    });
  };

  // Obtener el balance anterior para vista dual cuando ya hay un balance_sheet_code asignado.
  // Indicadores BPB del período anterior: se obtienen vía POST/PUT risk (previous_balance_sheet_code) y GET aplicación (risk_analysis.risks[BPB].previous_bpb_indicators).
  const getPreviousBalanceForDualView = (cuit: string, currentBalanceCode: string) => {
    getBalanceSheetsByCuit(cuit).then((response) => {
      if (hasData(response) && isSuccessfulResponse(response)) {
        const sortedBalances = [...response.data].sort((a: any, b: any) => {
          const dateA = new Date(a.to_year, (a.to_month || 1) - 1);
          const dateB = new Date(b.to_year, (b.to_month || 1) - 1);
          return dateB.getTime() - dateA.getTime();
        });
        const currentIndex = sortedBalances.findIndex(
          (balance: { code: string }) => balance.code === currentBalanceCode
        );
        if (currentIndex !== -1 && sortedBalances.length > currentIndex + 1) {
          const previousBalance = sortedBalances[currentIndex + 1];
          const stdPrev = standardizeBalanceData(previousBalance);
          setPreviousBalanceInfo({
            ...previousBalance,
            ...formatNumbers(stdPrev.values),
          });
          setPreviousLocalBalanceId(previousBalance.id);
        }
      }
    });
  };

  const formatNumbers = (obj: Record<string, any>) => {
    return Object.fromEntries(
      Object.entries(obj).map(([key, value]): [string, any] => {
        if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
          return [key, formatNumbers(value)];
        }
        // Convertir números a strings para que se muestren correctamente en los inputs
        if (typeof value === 'number') {
          return [key, String(value)];
        }
        return [key, value];
      })
    );
  };
  const handleRiskData = (
    riskCodes: string[],
    forceRefresh: boolean = false,
    analysisIdOverride?: number,
    options?: { silent?: boolean }
  ) => {
    if (!options?.silent) {
      setIsLoading(true);
    }
    const effectiveAnalysisId = analysisIdOverride ?? riskAnalysisId;

    const buildBody = () => {
      const withDual = showDualBalance && previousBalanceInfo?.code && riskCodes.includes('BPB');
      if (withDual) {
        return {
          codes: riskCodes,
          ...(forceRefresh && { force_refresh: true }),
          previous_balance_sheet_code: previousBalanceInfo!.code,
        };
      }
      if (forceRefresh) return { codes: riskCodes, force_refresh: true };
      return riskCodes;
    };

    const setPreviousIndicatorsFromRisks = (risks: riskData[]) => {
      const bpbRisk = risks?.find((r: riskData) => r.risk_code === 'BPB');
      const prev = bpbRisk?.previous_bpb_indicators ?? (bpbRisk as any)?.previousBpbIndicators;
      setPreviousBpbIndicators(Array.isArray(prev) && prev.length > 0 ? prev : undefined);
    };

    /** Keep previously seeded risks (e.g. BPB) if PUT/POST omits them. */
    const applyMergedRisks = (incoming: riskData[] | undefined) => {
      const next = Array.isArray(incoming) ? incoming : [];
      let merged: riskData[] = next;
      setRiskData((prev) => {
        const byCode = new Map<string, riskData>();
        (Array.isArray(prev) ? (prev as riskData[]) : []).forEach((r) => {
          if (r?.risk_code) byCode.set(r.risk_code, r);
        });
        next.forEach((r) => {
          if (r?.risk_code) byCode.set(r.risk_code, r);
        });
        merged = Array.from(byCode.values());
        return merged as unknown as riskData;
      });
      setPreviousIndicatorsFromRisks(merged);
    };

    const body = buildBody();
    if (effectiveAnalysisId) {
      putRiskData(cuit || '', id || '', body, effectiveAnalysisId)
        .then((response) => {
          if (hasData(response) && response.status === 200) {
            applyMergedRisks(response.data.risks);
          } else if (hasError(response).isAnyError) {
            useToast.error({
              title:
                'Ocurrió un error al consultar la información del balance.',
              id: 'risk-toast',
              description: hasError(response).isClientError
                ? 'Error del cliente'
                : 'Error del servidor',
              closeButton: true,
              autoClose: 5000,
            });
          }
          setIsLoading(false);
        })
        .catch(() => setIsLoading(false));
    } else {
      postRiskData(cuit || '', id || '', body)
        .then((response) => {
          if (hasData(response) && response.status === 201) {
            setRiskAnaliysisId(response.data.id);
            applyMergedRisks(response.data.risks);
          } else if (hasError(response).isAnyError) {
            useToast.error({
              title: 'Ocurrió un error al consultar la información del balance.',
              id: 'risk-toast',
              description: hasError(response).isClientError
                ? 'Error del cliente'
                : 'Error del servidor',
              closeButton: true,
              autoClose: 5000,
            });
          }
          setIsLoading(false);
        })
        .catch(() => setIsLoading(false));
    }
  };

  const handleUpdateDebts = (codes: string[]) => {
    if (debts?.code && cuit && id) {
      updateDebts(cuit, id, debts.code, codes).then((response) => {
        if (hasData(response) && response.status === 200) {
          setDebts(response.data);
          useToast.success({
            title: 'Deudas actualizadas correctamente',
            id: 'debts-toast',
            description: '',
            closeButton: true,
            autoClose: 3000,
          });
        } else if (hasError(response).isAnyError) {
          useToast.error({
            title: 'Ocurrió un error al actualizar las deudas.',
            id: 'debts-toast',
            description: hasError(response).isClientError
              ? 'Error del cliente'
              : 'Error del servidor',
            closeButton: true,
            autoClose: 5000,
          });
        }
      });
    }
  };

  const handleRiskDataAndDebts = () => {
    if (!cuit || !id) return;
    setIsLoading(true);
    getRiskAnalysisWithDebts(cuit, id, {
      codes: paso1RiskCodes(),
      force_refresh: true,
    })
      .then((response) => {
        const payload = response?.data as any;
        const combinedRisk = payload?.risk_analysis ?? payload?.riskAnalysis;
        if (hasData(response) && response.status >= 200 && response.status < 300 && combinedRisk) {
          setRiskData(normalizeRiskData(combinedRisk.risks ?? []));
          if (combinedRisk.id) setRiskAnaliysisId(combinedRisk.id);
          if (payload.debts) setDebts(payload.debts);
        } else if (hasError(response).isAnyError) {
          useToast.error({
            title: 'Ocurrió un error al consultar la información.',
            id: 'risk-toast',
            description: hasError(response).isClientError ? 'Error del cliente' : 'Error del servidor',
            closeButton: true,
            autoClose: 5000,
          });
        }
      })
      .catch(() => {
        useToast.error({
          title: 'Ocurrió un error al consultar la información.',
          id: 'risk-toast',
          description: 'Error del servidor',
          closeButton: true,
          autoClose: 5000,
        });
      })
      .finally(() => setIsLoading(false));
  };

  const steps = [
    {
      id: 1,
      value: 'Semáforo',
      element: (
        <CheckLight
          handleNextStep={() => {
            goToNextStep();
            updateMaxStepReached(2);
          }}
          riskData={riskData}
          isLoading={isLoading}
          handleRiskData={handleRiskData}
          handleUpdateDebts={handleUpdateDebts}
          handleRiskDataAndDebts={handleRiskDataAndDebts}
          cuit={cuit}
          applicationId={id}
        />
      ),
    },
    {
      id: 2,
      value: 'Carga de balance',
      element: showDualBalance ? (
        <DualBalanceLoader
          cuit={cuit}
          applicationId={id}
          balanceInfo={balanceInfo}
          handleNextStep={(e: any) => {
            setBalanceData(e);
            goToNextStep();
            updateMaxStepReached(3);
          }}
          handlePurchasesSalesData={(e: any) => setPurchasesSalesData(e)}
          setBalanceInfo={(e: any) => {
            const std = standardizeBalanceData(e.values);
            setBalanceInfo({
              ...(e.values as balanceInfo),
              ...formatNumbers(std.values),
            });
          }}
          setLocalBalanceId={(e: any) => setLocalBalanceId(e)}
          localBalanceId={localBalanceId}
          applicationStatus={applicationStatusKey}
          previousBalanceInfo={previousBalanceInfo}
          setPreviousBalanceInfo={(e: any) => {
            const std = standardizeBalanceData(e.values);
            setPreviousBalanceInfo({
              ...(e.values as balanceInfo),
              ...formatNumbers(std.values),
              code: e.code ?? previousBalanceInfo?.code,
            });
          }}
          setPreviousLocalBalanceId={(e: any) => setPreviousLocalBalanceId(e)}
          previousLocalBalanceId={previousLocalBalanceId}
          categoryType={client?.category_type}
          onBalanceSaved={(code: string) => cuit && getBalanceInfoFromCode(cuit, code)}
        />
      ) : (
        <BalanceLoader
          cuit={cuit}
          applicationId={id}
          balanceInfo={balanceInfo}
          handleNextStep={(e: any) => {
            setBalanceData(e);
            goToNextStep();
            updateMaxStepReached(3);
          }}
          handlePurchasesSalesData={(e: any) => setPurchasesSalesData(e)}
          setBalanceInfo={(e: any) => {
            const std = standardizeBalanceData(e.values);
            setBalanceInfo({
              ...(e.values as balanceInfo),
              ...formatNumbers(std.values),
            });
          }}
          setLocalBalanceId={(e: any) => setLocalBalanceId(e)}
          localBalanceId={localBalanceId}
          applicationStatus={applicationStatusKey}
          categoryType={client?.category_type}
          onBalanceSaved={(code: string) => cuit && getBalanceInfoFromCode(cuit, code)}
        />
      ),
    },
    {
      id: 3,
      value: 'Actividad post-balance',
      element: (
        <PostBalanceActivity
          cuit={cuit}
          applicationId={id}
          handleNextStep={() => {
            goToNextStep();
            updateMaxStepReached(4);
          }}
          handleRiskData={(e: any) => handleRiskData(e)}
          purchasesSalesData={purchasesSalesData}
          postBalanceData={postBalanceData}
          setPostBalanceData={(e: any) => setPostBalanceData(e)}
          applicationStatus={applicationStatusKey}
          balanceLastMonth={{
            to_year: balanceInfo?.to_year,
            to_month: balanceInfo?.to_month,
          }}
        />
      ),
    },
    {
      id: 4,
      value: 'Resumen de Riesgo',
      element: (
        <RiskResume
          riskData={riskData}
          isLoading={isLoading}
          handleRiskData={handleRiskData}
          handleRiskDataAndDebts={handleRiskDataAndDebts}
          cuit={cuit}
          applicationId={id}
          files={client?.files}
          legalEntity={client?.basic_data.legal_entity}
          applicationStatus={applicationStatusKey}
          debts={debts}
          declineOrChangeBtn={!showOnlyRiskResume}
          hideInfo={hideCommiteInfo}
          balanceData={balanceInfo}
          postBalanceData={postBalanceData}
          clientData={client}
          isMaxCreditCalculatorEnabled={isMaxCreditCalculatorEnabled}
          isQuotationsEnabled={isQuotationsEnabled}
          // Dual balance props (Cementos feature)
          showDualBalance={showDualBalance}
          previousBalanceData={previousBalanceInfo}
          previousBpbIndicators={previousBpbIndicators}
          isUruguayEnabled={isUruguayEnabled}
        />
      ),
    },
  ];

  return (
    <>
      {isUruguayEnabled && showStepBar && (
        <div
          style={{
            fontFamily: 'Inter, sans-serif',
            fontSize: 16,
            lineHeight: 1.2,
            boxSizing: 'border-box',
            justifySelf: 'stretch',
            width: '100%',
            marginTop: -24,
            padding: '18px 0 16px',
            transform: 'translate(43px, -27px)',
          }}
        >
          <span
            style={{
              color: theme.colors['secondary-2'].text.regular,
              fontWeight: 400,
            }}
          >
            Inicio
          </span>
          <span
            style={{
              color: theme.colors['secondary-2'].text.regular,
              display: 'inline-block',
              margin: '0 10px',
              textAlign: 'center',
              width: 5,
            }}
          >
            {'>'}
          </span>
          <span
            style={{
              color: theme.colors['secondary-2'].text.regular,
              fontWeight: 400,
            }}
          >
            Análisis de riesgos
          </span>
          <span
            style={{
              color: theme.colors['secondary-2'].text.regular,
              display: 'inline-block',
              margin: '0 10px',
              textAlign: 'center',
              width: 5,
            }}
          >
            {'>'}
          </span>
          <span style={{ color: '#1A1A1A', fontWeight: 700 }}>
            Solicitud {id || '123456'}
          </span>
        </div>
      )}
    <Card
      variant='neutral'
      modifier='inverted'
      height={isUruguayEnabled ? 'auto' : '-webkit-fill-available'}
      width={showOnlyRiskResume ? '100%' : '85vw'}
      spacing={isUruguayEnabled ? '3' : '4'}
      withElevation={!isUruguayEnabled}
      style={{
        ...(isUruguayEnabled
          ? {
              position: 'relative',
              background: 'transparent',
              isolation: 'isolate',
            }
          : {}),
        ...(showOnlyRiskResume
          ? {
              maxWidth: '100%',
              minWidth: 0,
              boxSizing: 'border-box' as const,
            }
          : {}),
      }}
    >
      {isUruguayEnabled && (
        <div
          aria-hidden
          data-lideco-frame
          style={{
            position: 'absolute',
            top: -31,
            // 35px mirrors the ~51px grey gutter on the left (sidebar→frame).
            // Must stay in sync with Checklight paddingRight (8.5 + 35).
            right: 35,
            bottom: 0,
            left: 59,
            borderRadius: 8,
            background: theme.colors.neutral.bg.inverted,
            boxShadow: theme.elevations['elevation-1'],
            pointerEvents: 'none',
            zIndex: -1,
          }}
        />
      )}
      <Toast id='risk-toast' />
      {showStepBar && (
        <Card margin={isUruguayEnabled ? '0 0 13px' : '0'}>
          <Card
            display='flex'
            margin={{
              bottom: isUruguayEnabled ? '2' : '4',
              top: isUruguayEnabled ? '0' : '0',
            }}
            gap={isUruguayEnabled ? '21px' : '10px'}
            alignItems='center'
            style={
              isUruguayEnabled
                ? { marginLeft: 14, transform: 'translate(60px, -18.5px)' }
                : undefined
            }
          >
            <Icon
              name='chevron_left_outline'
              height={24}
              width={24}
              cursor='pointer'
              onClick={handleGoBackToList}
            />
            {isUruguayEnabled ? (
              <div
                style={{
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  position: 'relative',
                }}
              >
                <H
                  withMargin={false}
                  type='2'
                  weight='bold'
                  modifier='strong'
                  style={{
                    color: theme.colors['secondary-2'].text.regular,
                    letterSpacing: 0.12,
                  }}
                >
                  {client?.basic_data?.company_name
                    ? `Análisis de riesgos de ${client.basic_data.company_name}`
                    : cuit
                      ? 'Análisis de riesgos de'
                      : 'Análisis de riesgos'}
                  {(cuit || client?.cuit) && (
                    <span
                      style={{
                        fontWeight: 400,
                        color: theme.colors['secondary-2'].text.regular,
                        marginLeft: 9,
                        fontSize: 19.5,
                        letterSpacing: 0,
                        position: 'relative',
                        top: 1,
                      }}
                    >
                      {cuit || client?.cuit || ''}
                    </span>
                  )}
                </H>
              </div>
            ) : (
              <H withMargin={false} type='2' weight='bold' modifier='strong'>
                {client?.basic_data?.company_name
                  ? 'Análisis de riesgos de ' +
                    capitalize(client.basic_data.company_name) +
                    ' ( ' +
                    cuitMask(cuit || client.cuit || '') +
                    ' )'
                  : cuit
                    ? 'Análisis de riesgos de ( ' + cuitMask(cuit) + ' )'
                    : 'Análisis de riesgos'}
              </H>
            )}
          </Card>
          {/* Atlantic stepper (#52582): title row, then centered track — same chrome UY and AR */}
          <Card
            display='flex'
            justifyContent='center'
            width='100%'
            margin={{ bottom: '1', top: '2' }}
            style={{ paddingTop: 16 }}
          >
            <div
              style={{
                width: 'min(600px, 88%)',
                maxWidth: 600,
                position: 'relative',
                paddingTop: 4,
                paddingBottom: 0,
                transform: isUruguayEnabled ? 'translate(28px, -17.5px)' : undefined,
              }}
            >
              <div
                aria-hidden
                style={{
                  position: 'absolute',
                  left: '10%',
                  right: '10%',
                  top: 20,
                  height: 1,
                  background: theme.colors.neutral.border.weaker,
                  pointerEvents: 'none',
                }}
              />
              <div style={{ display: 'flex', width: '100%', gap: 0, position: 'relative' }}>
                {steps.map((step, index) => (
                  <Step
                    key={step.id}
                    widthContainer='auto'
                    widthCircle1='0'
                    widthId='32px'
                    heightId='32px'
                    widthCircle2='0'
                    step={step}
                    stepsLength={steps.length}
                    index={index}
                    currentStep={currentStep}
                    changeStep={(s) => changeStep(s)}
                    atlanticStyle
                  />
                ))}
              </div>
            </div>
          </Card>
        </Card>
      )}

      {/*
        Uruguay: all steps share the same horizontal inset as the white
        data-lideco-frame (left:59 / right:35). Without this, only Semáforo
        (Checklight) stayed inside the card — Carga de balance / post-balance
        / resumen overflowed the right edge.
        Use overflow-x: clip (not hidden): hidden + overflow-y:visible forces
        overflow-y:auto per CSS, which nested on Resume creates double scrollbars.
      */}
      <div
        data-testid={
          showOnlyRiskResume ? 'risk-analysis-embed-content' : undefined
        }
        style={
          isUruguayEnabled
            ? {
                paddingLeft: 67,
                paddingRight: 43.5,
                boxSizing: 'border-box',
                width: '100%',
                maxWidth: '100%',
                minWidth: 0,
                overflowX: 'clip',
              }
            : showOnlyRiskResume
              ? {
                  boxSizing: 'border-box',
                  width: '100%',
                  maxWidth: '100%',
                  minWidth: 0,
                }
              : undefined
        }
      >
        {showOnlyRiskResume
          ? steps[steps.length - 1].element
          : steps[currentStep - 1].element}
      </div>
    </Card>
    </>
  );
};

export default RiskAnalysis;
